import React, { useEffect } from 'react'
import AdminDashboardLayout from './AdminDashboardLayout'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { base } from '../../constants/base'
import { getSingleProfessionalAPI } from '../../redux/slices/superAdminProfessionalSlice'
import './ProfileViewPage.css'

const ProfessionalViewPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const singleProfessional = useSelector(state => state?.superAdminProfessionalSlice?.singleProfessional);
    const loading = useSelector(state => state?.superAdminProfessionalSlice?.loading);
    const param = useParams();
    const professional_id = param?.id;

    const fetchSingleProfessional = async () => {
        await dispatch(getSingleProfessionalAPI(professional_id));
    }

    useEffect(() => {
        fetchSingleProfessional();
    }, [professional_id]);

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'status-active';
            case 'block':
            case 'blocked':
                return 'status-blocked';
            default:
                return 'status-pending';
        }
    };

    return (
        <AdminDashboardLayout>
            <div className="profile-view-page">
                {/* Page Header */}
                <div className="pv-header">
                    <button className="pv-back-btn" onClick={() => navigate('/admin/professionals')}>
                        <i className="fas fa-arrow-left"></i>
                        <span>Back to Professionals</span>
                    </button>
                    <h1 className="pv-title">Professional Details</h1>
                </div>

                {loading ? (
                    <div className="pv-loading">
                        <div className="pv-loader"></div>
                    </div>
                ) : (
                    <div className="pv-content">
                        {/* Profile Card */}
                        <div className="pv-profile-card">
                            <div className="pv-profile-header">
                                <div className="pv-avatar-section">
                                    {singleProfessional?.images?.[0]?.path ? (
                                        <img
                                            src={`${base.imageUrl}${singleProfessional?.images?.[0]?.path}`}
                                            alt={singleProfessional?.name}
                                            className="pv-avatar"
                                        />
                                    ) : (
                                        <div className="pv-avatar-placeholder">
                                            <i className="fas fa-user-tie"></i>
                                        </div>
                                    )}
                                </div>
                                <div className="pv-profile-info">
                                    <h2 className="pv-name">{singleProfessional?.name || '-'}</h2>
                                    <p className="pv-email">{singleProfessional?.email || '-'}</p>
                                    <div className="pv-badges">
                                        <span className="pv-role-badge">Professional</span>
                                        {singleProfessional?.status && (
                                            <span className={`pv-status-badge ${getStatusClass(singleProfessional?.status)}`}>
                                                {singleProfessional?.status}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Details Grid */}
                        <div className="pv-details-grid">
                            {/* Contact Information */}
                            <div className="pv-details-card">
                                <div className="pv-card-header">
                                    <i className="fas fa-address-book"></i>
                                    <h3>Contact Information</h3>
                                </div>
                                <div className="pv-card-body">
                                    {singleProfessional?.phone_no && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-phone"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Phone Number</span>
                                                <span className="pv-info-value">{singleProfessional?.phone_no}</span>
                                            </div>
                                        </div>
                                    )}
                                    {singleProfessional?.address?.address && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-location-dot"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Address</span>
                                                <span className="pv-info-value">{singleProfessional?.address?.address}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Identification */}
                            <div className="pv-details-card">
                                <div className="pv-card-header">
                                    <i className="fas fa-id-card"></i>
                                    <h3>Identification</h3>
                                </div>
                                <div className="pv-card-body">
                                    {singleProfessional?.ss_number && (
                                        <div className="pv-info-row">
                                            <div className="pv-info-icon">
                                                <i className="fas fa-address-card"></i>
                                            </div>
                                            <div className="pv-info-content">
                                                <span className="pv-info-label">Social Security Number</span>
                                                <span className="pv-info-value">{singleProfessional?.ss_number}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Documents */}
                            {(singleProfessional?.images?.[1]?.path || singleProfessional?.images?.[2]?.path) && (
                                <div className="pv-details-card pv-full-width">
                                    <div className="pv-card-header">
                                        <i className="fas fa-folder-open"></i>
                                        <h3>Documents</h3>
                                    </div>
                                    <div className="pv-card-body">
                                        <div className="pv-documents-grid">
                                            {singleProfessional?.images?.[1]?.path && (
                                                <a
                                                    href={`${base.imageUrl}${singleProfessional?.images?.[1]?.path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="pv-document-item"
                                                >
                                                    <div className="pv-doc-icon">
                                                        <i className="fas fa-certificate"></i>
                                                    </div>
                                                    <div className="pv-doc-info">
                                                        <span className="pv-doc-name">Professional Certificate</span>
                                                        <span className="pv-doc-action">Click to view</span>
                                                    </div>
                                                    <i className="fas fa-external-link-alt pv-doc-link"></i>
                                                </a>
                                            )}
                                            {singleProfessional?.images?.[2]?.path && (
                                                <a
                                                    href={`${base.imageUrl}${singleProfessional?.images?.[2]?.path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="pv-document-item"
                                                >
                                                    <div className="pv-doc-icon">
                                                        <i className="fas fa-file-pdf"></i>
                                                    </div>
                                                    <div className="pv-doc-info">
                                                        <span className="pv-doc-name">Other Supporting Document</span>
                                                        <span className="pv-doc-action">Click to view</span>
                                                    </div>
                                                    <i className="fas fa-external-link-alt pv-doc-link"></i>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* About */}
                            {singleProfessional?.about && (
                                <div className="pv-details-card pv-full-width">
                                    <div className="pv-card-header">
                                        <i className="fas fa-info-circle"></i>
                                        <h3>About</h3>
                                    </div>
                                    <div className="pv-card-body">
                                        <p className="pv-about-text">{singleProfessional?.about}</p>
                                    </div>
                                </div>
                            )}

                            {/* Services */}
                            {singleProfessional?.services?.length > 0 && (
                                <div className="pv-details-card pv-full-width">
                                    <div className="pv-card-header">
                                        <i className="fas fa-briefcase"></i>
                                        <h3>Services</h3>
                                    </div>
                                    <div className="pv-card-body">
                                        <div className="pv-services-list">
                                            {singleProfessional?.services?.map((item, index) => (
                                                <div key={index} className="pv-service-item">
                                                    <div className="pv-service-icon">
                                                        <i className="fas fa-check"></i>
                                                    </div>
                                                    <div className="pv-service-info">
                                                        <span className="pv-service-name">{item?.name}</span>
                                                        {item?.price && (
                                                            <span className="pv-service-price">
                                                                {item?.currency === 'USD' ? '$' : item?.currency === 'POUND' ? '£' : '₨'}
                                                                {item?.price}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </AdminDashboardLayout>
    )
}

export default ProfessionalViewPage
